import { Dialog, Slide } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankOutlinedIcon from '@material-ui/icons/CheckBoxOutlineBlankOutlined';
import CloseIcon from '@material-ui/icons/Close';
import CSS from 'csstype';
import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { FILTER_PILLS, FilterPillKey } from '~/src/dash/components/filters/common';
import { MobileTextInput } from '~/src/dash/components/input/MobileTextInput';
import { Col, Cols, Row, Rows, spacing, Text, TextButton } from '~/src/dash/publicaUI';
import { IListItem } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';

const checkboxStyle: CSS.Properties = {
  marginRight: spacing.small,
  color: publicaColors.darkGray,
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='left' ref={ref} {...props} />;
});

const sortList = (list: IListItem[]) => {
  const sorted = list.sort((a, b) => {
    if ((a.selected && b.selected) || (!a.selected && !b.selected)) {
      return a.title.localeCompare(b.title);
    } else if ((!a.selected && b.selected) || (a.selected && !b.selected)) {
      return a.selected > b.selected ? -1 : 1;
    } else {
      return 0;
    }
  });
  return sorted;
};

const queryList = (query: string, list: IListItem[]) => {
  let queriedList = cloneDeep(list);
  if (query) {
    const casedQuery = query.toLowerCase();
    queriedList = list.filter((item) => {
      if (item.ID.includes(query) || item.title.toLowerCase().includes(casedQuery)) {
        return true;
      }
      return false;
    });
  }
  return queriedList;
};

const getSelectedCountFromSorted = (list: IListItem[]) => {
  let count = 0;
  for (const item of list) {
    if (item.selected) {
      count++;
    } else {
      break;
    }
  }
  return count;
};

interface IFilterModalProps {
  open: boolean;
  options: Record<string, IListItem>;
  handleClose: () => void;
  filterKey: string;
  handleAccept: (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => void;
  selectedLimit?: number; // sets the maximum amount of options than can be selected
  displayID?: boolean; // determines whether to display the option ID
  currentSelected: Record<string, IListItem>; // current object of selected options
}

export const FilterModal = ({
  open,
  handleClose,
  options,
  filterKey,
  handleAccept,
  selectedLimit,
  displayID = false,
  currentSelected = {},
}: IFilterModalProps) => {
  const isMounted = useRef(false);
  const [sortedList, setSortedList] = useState<IListItem[]>([]);
  const [filteredList, setFilteredList] = useState<IListItem[]>([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [query, setQuery] = useState('');

  const isSingleSelect = selectedLimit === 1;

  const totalCount = Object.values(options).length;
  const label = filterKey ? FILTER_PILLS[filterKey as FilterPillKey].menuTitle : '';

  // sort options so that selected floats to the top
  useEffect(() => {
    const listArray = Object.values(options);
    const sorted = sortList(listArray);
    unstable_batchedUpdates(() => {
      setSortedList(sorted);
      setSelectedCount(getSelectedCountFromSorted(sorted));
      setQuery('');
    });
  }, [options]);

  // filter options upon search
  useEffect(() => {
    if (isMounted.current) {
      const queriedList = queryList(query, sortedList);
      setFilteredList(queriedList);
    }
    isMounted.current = true;
  }, [query, sortedList]);

  const handleChange = (id: string) => {
    if (isSingleSelect) {
      if (options[id].selected) {
        options[id].selected = false;
      } else if (selectedCount === 1) {
        const previouslySelectedID = sortedList[0].ID;
        options[previouslySelectedID].selected = false;
        options[id].selected = true;
      } else {
        options[id].selected = true;
      }
      const sorted = sortList(Object.values(options));
      unstable_batchedUpdates(() => {
        setSortedList(sorted);
        setSelectedCount(getSelectedCountFromSorted(sorted));
      });
      return;
    }
    if (
      !selectedLimit ||
      options[id].selected ||
      (!options[id].selected && selectedLimit && selectedCount < selectedLimit)
    ) {
      options[id].selected = !options[id].selected;
      const sorted = sortList(Object.values(options));
      unstable_batchedUpdates(() => {
        setSortedList(sorted);
        setSelectedCount(getSelectedCountFromSorted(sorted));
      });
      return;
    }
  };

  const handleClear = () => {
    Object.keys(options).forEach((key) => {
      options[key].selected = false;
    });
    const sorted = sortList(Object.values(options));
    unstable_batchedUpdates(() => {
      setSelectedCount(0);
      setSortedList(sorted);
    });
  };

  const handleApply = () => {
    const selected = {} as Record<string, IListItem>;
    if (selectedCount < totalCount) {
      for (const item of sortedList) {
        if (item.selected) {
          selected[item.ID] = item;
        } else {
          break;
        }
      }
    } else {
      // if all options are selected, set them all to false
      for (const item of sortedList) {
        options[item.ID].selected = false;
      }
    }
    handleAccept(filterKey, options, selected);
  };

  const handleReset = () => {
    // first we loop over the selected in the modal to check if they should be made false
    for (const item of sortedList) {
      const { ID } = item;
      if (item.selected && currentSelected) {
        if (!currentSelected[ID]) {
          options[ID].selected = false;
        }
      } else {
        break;
      }
    }
    // then we loop over the original selected to re-add all that should be selected true
    Object.keys(currentSelected).forEach((key) => {
      options[key].selected = true;
    });
    handleClose();
  };

  return (
    <Dialog
      fullScreen
      open={open}
      // @ts-ignore
      TransitionComponent={Transition}
    >
      <Rows style={{ overflow: 'hidden' }}>
        {/* STICKY SECTION */}
        <Row style={{ padding: '0px 0px 8px 0px' }}>
          <Rows gap={spacing.small}>
            <Row
              style={{
                padding: '12px 16px 8px',
                borderBottom: `1px solid ${publicaColors.lightGray}`,
              }}
            >
              <Cols
                gap={spacing.base}
                style={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Col>
                  <TextButton allCaps onClick={handleApply}>
                    Apply
                  </TextButton>
                </Col>
                <Col style={{ textAlign: 'right' }}>
                  <CloseIcon onClick={handleReset} style={{ width: '18px' }} />
                </Col>
              </Cols>
            </Row>
            <Row noVerticalPadding>
              <Text bold>{label}</Text>
            </Row>
            <Row noVerticalPadding>
              <MobileTextInput
                enableClear
                handleOnChange={setQuery}
                msDelay={500}
                placeholder={`Search ${label}`}
                prefix='search'
              />
            </Row>
            <Row noVerticalPadding>
              <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                <Col>
                  <Text muted small>
                    {selectedLimit
                      ? `${selectedCount} /${selectedLimit} Selected`
                      : `${selectedCount} Selected`}
                  </Text>
                </Col>
                {selectedCount > 0 && (
                  <Col style={{ paddingTop: '1px' }}>
                    <TextButton size='small' onClick={handleClear}>
                      Clear
                    </TextButton>
                  </Col>
                )}
              </Cols>
            </Row>
          </Rows>
        </Row>
        <Row style={{ height: '100%', overflowY: 'auto', padding: '0px 16px 32px' }}>
          <Rows gap={spacing.extraSmall}>
            {isEmpty(filteredList) && (
              <Row style={{ textAlign: 'center' }}>
                <Text small>No Available Options</Text>
              </Row>
            )}
            {filteredList.map((item) => {
              return (
                <Row noPadding onClick={() => handleChange(item.ID)}>
                  <Cols gap={spacing.small} style={{ alignItems: 'flex-start' }}>
                    <Col>
                      {item.selected ? (
                        <CheckBoxIcon style={checkboxStyle} />
                      ) : (
                        <CheckBoxOutlineBlankOutlinedIcon style={checkboxStyle} />
                      )}
                    </Col>
                    {displayID && (
                      <Col style={{ minWidth: '40px', paddingTop: '1px' }}>
                        <Text small muted bold={item.selected}>
                          {item.ID}
                        </Text>
                      </Col>
                    )}
                    <Col style={{ paddingTop: '1px' }}>
                      <Text small bold={item.selected}>
                        {item.title}
                      </Text>
                    </Col>
                  </Cols>
                </Row>
              );
            })}
          </Rows>
        </Row>
      </Rows>
    </Dialog>
  );
};
